/* ===================================================================
   NOVAE — storefront interactions. Vanilla JS, no dependencies.
   Grows as components are added (countdown, sticky header, …).
   =================================================================== */
(function () {
  'use strict';

  /* ---------- Scroll reveal (.novae-reveal -> .is-visible) ----------- */
  function initReveal(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var els = scope.querySelectorAll('.novae-reveal:not(.is-visible)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    els.forEach(function (el) { io.observe(el); });
  }
  if (document.readyState !== 'loading') initReveal();
  else document.addEventListener('DOMContentLoaded', function () { initReveal(); });
  document.addEventListener('shopify:section:load', function (e) { initReveal(e.target); });

  /* ---------- <novae-countdown data-deadline="2026-07-01 18:00"> ----- */
  class NovaeCountdown extends HTMLElement {
    connectedCallback() {
      this.deadline = this.parse(this.getAttribute('data-deadline'));
      this.expiredText = this.getAttribute('data-expired') || '';
      this.els = {
        days: this.querySelector('[data-days]'),
        hours: this.querySelector('[data-hours]'),
        mins: this.querySelector('[data-mins]'),
        secs: this.querySelector('[data-secs]'),
      };
      if (this.deadline === null) { this.hide(); return; }
      this.tick();
      this.timer = setInterval(this.tick.bind(this), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    parse(v) {
      if (!v) return null;
      var t = Date.parse(v.indexOf('T') === -1 ? v.replace(' ', 'T') : v);
      return isNaN(t) ? null : t;
    }
    pad(n) { return (n < 10 ? '0' : '') + n; }
    tick() {
      var diff = this.deadline - Date.now();
      if (diff <= 0) {
        clearInterval(this.timer);
        this.innerHTML = this.expiredText
          ? '<span class="novae-countdown__label">' + this.expiredText + '</span>'
          : '';
        if (!this.expiredText) this.hide();
        this.dispatchEvent(new CustomEvent('novae:countdown:end', { bubbles: true }));
        return;
      }
      var s = Math.floor(diff / 1000);
      var d = Math.floor(s / 86400); s -= d * 86400;
      var h = Math.floor(s / 3600); s -= h * 3600;
      var m = Math.floor(s / 60); s -= m * 60;
      if (this.els.days) this.els.days.textContent = d;
      if (this.els.hours) this.els.hours.textContent = this.pad(h);
      if (this.els.mins) this.els.mins.textContent = this.pad(m);
      if (this.els.secs) this.els.secs.textContent = this.pad(s);
    }
    hide() { this.style.display = 'none'; }
  }
  if (!customElements.get('novae-countdown')) {
    customElements.define('novae-countdown', NovaeCountdown);
  }

  /* ---------- <novae-counter data-target="25000" data-suffix="+"> ---- */
  class NovaeCounter extends HTMLElement {
    connectedCallback() {
      this.target = parseFloat(this.getAttribute('data-target')) || 0;
      this.prefix = this.getAttribute('data-prefix') || '';
      this.suffix = this.getAttribute('data-suffix') || '';
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.target <= 0 || reduce || !('IntersectionObserver' in window)) {
        this.render(this.target);
        return;
      }
      var self = this;
      this.io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) { self.animate(); self.io.disconnect(); }
          });
        },
        { threshold: 0.4 }
      );
      this.io.observe(this);
    }
    format(n) {
      var rounded = Math.round(n);
      try { return this.prefix + rounded.toLocaleString() + this.suffix; }
      catch (e) { return this.prefix + rounded + this.suffix; }
    }
    render(n) { this.textContent = this.format(n); }
    animate() {
      var dur = 1600, start = null, self = this;
      function step(ts) {
        if (start === null) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        self.render(self.target * (1 - Math.pow(1 - p, 3)));
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }
  }
  if (!customElements.get('novae-counter')) {
    customElements.define('novae-counter', NovaeCounter);
  }

  /* ---------- Quick-add + in-drawer upsell (event delegation) -------- */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    var toggle = e.target.closest('[data-novae-qa-toggle]');
    if (toggle) {
      e.preventDefault();
      var wrap = toggle.closest('novae-quick-add, .novae-card__qa-wrap');
      var panel = wrap && wrap.querySelector('.novae-card__qa-panel');
      if (panel) {
        var isOpen = !panel.hasAttribute('hidden');
        document.querySelectorAll('.novae-card__qa-panel:not([hidden])').forEach(function (pn) {
          if (pn !== panel) {
            pn.setAttribute('hidden', '');
            var ot = pn.closest('novae-quick-add, .novae-card__qa-wrap');
            var otb = ot && ot.querySelector('[data-novae-qa-toggle]');
            if (otb) otb.setAttribute('aria-expanded', 'false');
          }
        });
        panel.toggleAttribute('hidden', isOpen);
        toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      }
      return;
    }

    var addBtn = e.target.closest('[data-novae-add]');
    if (addBtn && !addBtn.disabled) {
      e.preventDefault();
      novaeCartAdd(addBtn);
    }
  });

  async function novaeCartAdd(btn) {
    var id = btn.getAttribute('data-variant-id');
    if (!id) return;
    var cartDrawer = document.querySelector('cart-drawer');
    btn.setAttribute('aria-busy', 'true');
    try {
      var sectionIds = 'cart-drawer,cart-icon-bubble';
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        sectionIds = cartDrawer
          .getSectionsToRender()
          .map(function (s) { return s.id; })
          .join(',');
      }
      var url = (window.routes && window.routes.cart_add_url) || '/cart/add';
      var res = await fetch(url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1 }],
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      });
      var data = await res.json();
      if (res.ok && cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(data);
      } else if (res.ok && !cartDrawer) {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      }
      var openPanel = btn.closest('.novae-card__qa-panel');
      if (openPanel) {
        openPanel.setAttribute('hidden', '');
        var w = openPanel.closest('novae-quick-add, .novae-card__qa-wrap');
        var tb = w && w.querySelector('[data-novae-qa-toggle]');
        if (tb) tb.setAttribute('aria-expanded', 'false');
      }
    } catch (err) {
      /* keep the buy flow resilient — fail silently */
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }

  /* ---------- Size-guide modal -------------------------------------- */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var openBtn = e.target.closest('[data-novae-sizeguide-open]');
    if (openBtn) {
      var wrap = openBtn.closest('.novae-sizeguide');
      var dlg = wrap && wrap.querySelector('[data-novae-sizeguide]');
      if (dlg && typeof dlg.showModal === 'function') dlg.showModal();
      return;
    }
    var closeBtn = e.target.closest('[data-novae-sizeguide-close]');
    if (closeBtn) {
      var d = closeBtn.closest('[data-novae-sizeguide]');
      if (d) d.close();
      return;
    }
    if (e.target.matches && e.target.matches('[data-novae-sizeguide]')) {
      e.target.close(); // backdrop click
    }
  });

  /* ---------- <novae-sticky-atc> (mobile sticky add-to-cart) -------- */
  class NovaeStickyAtc extends HTMLElement {
    connectedCallback() {
      this.addBtn = this.querySelector('.novae-sticky-atc__add');
      if (this.addBtn) this.addBtn.addEventListener('click', this.proxyAdd.bind(this));
      this.target =
        document.querySelector('product-form .product-form__submit') ||
        document.querySelector('product-form button[name="add"]') ||
        document.querySelector('.product-form__buttons');
      if (this.target && 'IntersectionObserver' in window) {
        var self = this;
        this.io = new IntersectionObserver(
          function (entries) { entries.forEach(function (en) { self.toggle(!en.isIntersecting); }); },
          { rootMargin: '0px 0px -10% 0px' }
        );
        this.io.observe(this.target);
      }
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); }
    toggle(show) { this.toggleAttribute('hidden', !show); }
    proxyAdd(e) {
      e.preventDefault();
      var btn =
        document.querySelector('product-form .product-form__submit') ||
        document.querySelector('product-form button[name="add"]');
      if (btn) btn.click();
    }
  }
  if (!customElements.get('novae-sticky-atc')) {
    customElements.define('novae-sticky-atc', NovaeStickyAtc);
  }
})();
